/** @file useCompassDrag: a left drag on the orientation cube orbits the camera yaw; a click still snaps. */
import { useEffect, type RefObject } from 'react';
import type { EditorStore } from '../../state/editorTypes';

/** Pointer travel (CSS px) before a press becomes a drag; degrees of yaw per px (the hand tool's orbit rate). */
const THRESHOLD_PX = 4;
const DEG_PER_PX = 1;

/** A press in progress: its pointer, where it started, the yaw then, and whether it became a drag. */
type Press = { id: number; x: number; y: number; yaw: number; dragging: boolean };

/** `deg` in [0, 360). */
const wrap = (deg: number): number => ((deg % 360) + 360) % 360;

/**
 * On `ref` (the cube's svg): a left press that travels `THRESHOLD_PX` turns into a drag that sets the camera yaw to the
 * start yaw + `dx` x `DEG_PER_PX` (like the round compass of TK's reference editor; the pointer is captured once
 * dragging), and the click that ends a drag is swallowed so no face snaps; a press without travel stays a click.
 */
export function useCompassDrag(ref: RefObject<SVGSVGElement | null>, store: EditorStore): void {
  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    let press: Press | null = null;
    let swallow = false;
    const down = (e: PointerEvent): void => {
      swallow = false;
      press = e.button === 0 ? { id: e.pointerId, x: e.clientX, y: e.clientY, yaw: store.getState().camera.yawDeg, dragging: false } : null;
    };
    const move = (e: PointerEvent): void => {
      if (press?.id !== e.pointerId) return;
      const dx = e.clientX - press.x;
      if (!press.dragging && Math.hypot(dx, e.clientY - press.y) < THRESHOLD_PX) return;
      if (!press.dragging) (el as Partial<Pick<Element, 'setPointerCapture'>>).setPointerCapture?.(e.pointerId);
      press.dragging = true;
      store.getState().setCamera({ yawDeg: wrap(press.yaw + dx * DEG_PER_PX) });
    };
    const up = (): void => { swallow = press?.dragging === true; press = null; };
    const click = (e: MouseEvent): void => { if (swallow) { swallow = false; e.stopPropagation(); } };
    const listeners: [string, EventListener, boolean][] = [
      ['pointerdown', down as EventListener, false], ['pointermove', move as EventListener, false], ['pointerup', up, false],
      ['pointercancel', up, false], ['click', click as EventListener, true],
    ];
    for (const [type, fn, capture] of listeners) el.addEventListener(type, fn, capture);
    return () => { for (const [type, fn, capture] of listeners) el.removeEventListener(type, fn, capture); };
  }, [ref, store]);
}
